import type { RealtimeChannel } from '@supabase/realtime-js'
import { RealtimeClient } from '@supabase/realtime-js'
import { getRealtimeBaseUrl } from '../utils/realtime'
import { getLoggedinUser } from '../helpers/api_helper'
import { getRealtimeAccessToken, subscribeToTokenUpdates } from './realtimeAuth'
import realtimeLogger from './realtimeLogger'

type SupportTicketEvent =
  | 'support-ticket:created'
  | 'support-ticket:assigned'
  | 'support-ticket:status-changed'
  | 'support-ticket:closed'
  | 'support-ticket:update'
  | 'support-ticket:assignment'

type SupportTicketPayload = {
  event: SupportTicketEvent
  support_ticket_id: number
  business_id?: number
  data: Record<string, unknown>
}

export type SupportTicketListener = (payload: SupportTicketPayload) => void

class SupportTicketManager {
  private client: RealtimeClient | null = null
  private readonly channels = new Map<string, RealtimeChannel>()
  private readonly listeners = new Map<string, Set<SupportTicketListener>>()
  private tokenUnsubscribe: (() => void) | null = null

  constructor() {
    // Subscribe to token updates
    this.tokenUnsubscribe = subscribeToTokenUpdates((newToken) => {
      this.handleTokenUpdate(newToken)
    })
  }

  /**
   * Handle token update by updating the client's access token
   */
  private handleTokenUpdate(newToken: string): void {
    if (!this.client) {
      console.log('[realtimeSupportTickets] No client to update with new token')
      return
    }

    console.log('[realtimeSupportTickets] Updating client with new token')
    
    try {
      // Update the access token in the client
      this.client.setAuth(newToken)
      console.log('[realtimeSupportTickets] Client token updated successfully')
    } catch (error) {
      console.error('[realtimeSupportTickets] Failed to update client token:', error)
    }
  }

  private getAnonKey(): string | null {
    return (
      import.meta.env.VITE_SUPABASE_REALTIME_ANON_KEY ??
      import.meta.env.VITE_SUPABASE_ANON_KEY ??
      null
    )
  }

  private async ensureClient(): Promise<RealtimeClient | null> {
    // Check if user is authenticated before creating client
    const auth = getLoggedinUser()
    if (!auth || !auth.token) {
      console.log('[realtimeSupportTickets] User not authenticated, skipping client creation')
      return null
    }

    if (!this.client) {
      // Get user's JWT token for authentication
      const userToken = await getRealtimeAccessToken()
      if (!userToken) {
        console.error('[realtimeSupportTickets] Failed to get realtime access token')
        return null
      }

      console.log('[realtimeSupportTickets] Creating Realtime client with user JWT token')
      this.client = new RealtimeClient(getRealtimeBaseUrl(), {
        params: {
          apikey: userToken,  // Use user's JWT token for authentication
          vsn: '1.0.0',
        },
        accessToken: getRealtimeAccessToken,  // Provide callback for token refresh
      })
      this.client.connect()
    }

    return this.client
  }

  /**
   * Subscribe to a specific support ticket channel
   * @param ticketId - The support ticket ID
   * @param listener - Callback function to handle ticket events
   * @returns Unsubscribe function
   */
  subscribe(ticketId: number | string, listener: SupportTicketListener): () => void {
    const channelName = `support-ticket:${ticketId}`
    const existing = this.listeners.get(channelName)

    if (existing) {
      existing.add(listener)
    } else {
      this.listeners.set(channelName, new Set([listener]))
      // Create channel asynchronously
      this.createTicketChannel(ticketId).catch((error) => {
        console.error('[realtimeSupportTickets] Failed to create ticket channel:', error)
      })
      realtimeLogger.logSubscription(channelName, 'support-ticket')
    }

    return () => {
      this.detach(channelName, listener)
    }
  }

  /**
   * Subscribe to all support tickets for a specific user
   * @param userId - The user ID
   * @param listener - Callback function to handle ticket events
   * @returns Unsubscribe function
   */
  subscribeToUserTickets(userId: number | string, listener: SupportTicketListener): () => void {
    const channelName = `user:${userId}`
    const existing = this.listeners.get(channelName)

    if (existing) {
      existing.add(listener)
    } else {
      this.listeners.set(channelName, new Set([listener]))
      // Create channel asynchronously
      this.createUserChannel(userId).catch((error) => {
        console.error('[realtimeSupportTickets] Failed to create user channel:', error)
      })
      realtimeLogger.logSubscription(channelName, 'user-support-tickets')
    }

    return () => {
      this.detach(channelName, listener)
    }
  }

  /**
   * Subscribe to all support tickets for a specific business
   * @param businessId - The business ID
   * @param listener - Callback function to handle ticket events
   * @returns Unsubscribe function
   */
  subscribeToBusinessTickets(businessId: number | string, listener: SupportTicketListener): () => void {
    const channelName = `business:${businessId}`
    const existing = this.listeners.get(channelName)

    if (existing) {
      existing.add(listener)
    } else {
      this.listeners.set(channelName, new Set([listener]))
      // Create channel asynchronously
      this.createBusinessChannel(businessId).catch((error) => {
        console.error('[realtimeSupportTickets] Failed to create business channel:', error)
      })
      realtimeLogger.logSubscription(channelName, 'business-support-tickets')
    }

    return () => {
      this.detach(channelName, listener)
    }
  }

  private async createTicketChannel(ticketId: number | string): Promise<RealtimeChannel | null> {
    const client = await this.ensureClient()
    if (!client) {
      return null
    }

    const channelName = `support-ticket:${ticketId}`
    const existing = this.channels.get(channelName)
    if (existing) {
      return existing
    }

    const channel = client.channel(channelName, {
      config: {
        broadcast: {
          ack: false,
          self: false,
        },
      },
    })

    // Listen for all support ticket events
    this.setupTicketEventListeners(channel, channelName)

    channel.subscribe((status) => {
      if (status === 'CHANNEL_ERROR') {
        console.warn(`[realtimeSupportTickets] Channel error on ${channelName}`)
      }
      if (status === 'SUBSCRIBED') {
        console.log(`[realtimeSupportTickets] Subscribed to ${channelName}`)
      }
      if (status === 'CLOSED') {
        this.channels.delete(channelName)
      }
    })

    this.channels.set(channelName, channel)
    return channel
  }

  private async createUserChannel(userId: number | string): Promise<RealtimeChannel | null> {
    const client = await this.ensureClient()
    if (!client) {
      return null
    }

    const channelName = `user:${userId}`
    const existing = this.channels.get(channelName)
    if (existing) {
      return existing
    }

    const channel = client.channel(channelName, {
      config: {
        broadcast: {
          ack: false,
          self: false,
        },
      },
    })

    // Listen for support ticket events on user channel
    this.setupTicketEventListeners(channel, channelName)

    channel.subscribe((status) => {
      if (status === 'CHANNEL_ERROR') {
        console.warn(`[realtimeSupportTickets] Channel error on ${channelName}`)
      }
      if (status === 'SUBSCRIBED') {
        console.log(`[realtimeSupportTickets] Subscribed to ${channelName}`)
      }
      if (status === 'CLOSED') {
        this.channels.delete(channelName)
      }
    })

    this.channels.set(channelName, channel)
    return channel
  }

  private async createBusinessChannel(businessId: number | string): Promise<RealtimeChannel | null> {
    const client = await this.ensureClient()
    if (!client) {
      return null
    }

    const channelName = `business:${businessId}`
    const existing = this.channels.get(channelName)
    if (existing) {
      return existing
    }

    const channel = client.channel(channelName, {
      config: {
        broadcast: {
          ack: false,
          self: false,
        },
      },
    })

    // Listen for support ticket events on business channel
    channel.on('broadcast', { event: 'support-ticket:created' }, ({ payload }) => {
      this.emit(channelName, payload as SupportTicketPayload)
    })

    channel.on('broadcast', { event: 'support-ticket:updated' }, ({ payload }) => {
      this.emit(channelName, payload as SupportTicketPayload)
    })

    channel.subscribe((status) => {
      if (status === 'CHANNEL_ERROR') {
        console.warn(`[realtimeSupportTickets] Channel error on ${channelName}`)
      }
      if (status === 'SUBSCRIBED') {
        console.log(`[realtimeSupportTickets] Subscribed to ${channelName}`)
      }
      if (status === 'CLOSED') {
        this.channels.delete(channelName)
      }
    })

    this.channels.set(channelName, channel)
    return channel
  }

  private setupTicketEventListeners(channel: RealtimeChannel, channelName: string): void {
    // Listen for ticket created events
    channel.on('broadcast', { event: 'support-ticket:created' }, ({ payload }) => {
      this.emit(channelName, payload as SupportTicketPayload)
    })

    // Listen for ticket assigned events
    channel.on('broadcast', { event: 'support-ticket:assigned' }, ({ payload }) => {
      this.emit(channelName, payload as SupportTicketPayload)
    })

    // Listen for ticket status changed events
    channel.on('broadcast', { event: 'support-ticket:status-changed' }, ({ payload }) => {
      this.emit(channelName, payload as SupportTicketPayload)
    })

    // Listen for ticket closed events
    channel.on('broadcast', { event: 'support-ticket:closed' }, ({ payload }) => {
      this.emit(channelName, payload as SupportTicketPayload)
    })

    // Listen for ticket update events
    channel.on('broadcast', { event: 'support-ticket:update' }, ({ payload }) => {
      this.emit(channelName, payload as SupportTicketPayload)
    })

    // Listen for ticket assignment events
    channel.on('broadcast', { event: 'support-ticket:assignment' }, ({ payload }) => {
      this.emit(channelName, payload as SupportTicketPayload)
    })
  }

  private emit(channelName: string, payload: SupportTicketPayload): void {
    const listeners = this.listeners.get(channelName)
    if (!listeners || listeners.size === 0) return

    Array.from(listeners).forEach((listener) => {
      try {
        listener(payload)
      } catch (error) {
        console.error('[realtimeSupportTickets] Listener error', error)
        realtimeLogger.logEventError(channelName, payload.event, error as Error)
      }
    })
  }

  private detach(channelName: string, listener?: SupportTicketListener): void {
    const listeners = this.listeners.get(channelName)
    if (!listeners) return

    if (listener) {
      listeners.delete(listener)
    }

    // Clean up channel if no more listeners
    if (listeners.size === 0) {
      this.listeners.delete(channelName)
      const channel = this.channels.get(channelName)
      if (channel) {
        channel.unsubscribe()
        this.channels.delete(channelName)
        console.log(`[realtimeSupportTickets] Unsubscribed from ${channelName}`)
        realtimeLogger.logUnsubscription(channelName, 'No more listeners')
      }
    }
  }

  /**
   * Disconnect all channels and clean up
   */
  disconnectAll(): void {
    this.channels.forEach((channel, channelName) => {
      channel.unsubscribe()
      console.log(`[realtimeSupportTickets] Disconnected from ${channelName}`)
    })
    this.channels.clear()
    this.listeners.clear()
    this.client?.disconnect()
    this.client = null

    // Unsubscribe from token updates
    if (this.tokenUnsubscribe) {
      this.tokenUnsubscribe()
      this.tokenUnsubscribe = null
    }
  }
}

// Export singleton instance
const supportTicketManager = new SupportTicketManager()

// Export convenience functions
export const subscribeToSupportTicket = (
  ticketId: number | string,
  listener: SupportTicketListener
): (() => void) => {
  return supportTicketManager.subscribe(ticketId, listener)
}

export const subscribeToUserSupportTickets = (
  userId: number | string,
  listener: SupportTicketListener
): (() => void) => {
  return supportTicketManager.subscribeToUserTickets(userId, listener)
}

export const subscribeToBusinessSupportTickets = (
  businessId: number | string,
  listener: SupportTicketListener
): (() => void) => {
  return supportTicketManager.subscribeToBusinessTickets(businessId, listener)
}

export default supportTicketManager
